import React, { useEffect, useState } from 'react';
import { 
  Truck, 
  Star, 
  ShieldCheck, 
  ArrowLeft, 
  ShoppingBag,
  Info 
} from 'lucide-react';
import { productsAPI } from '../services/api';
import { Link, useNavigate, useParams } from 'react-router-dom';

const PublicProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const numericId = String(id).split('-')[0];
        const res = await productsAPI.getById(numericId);
        setProduct(res.data.product || res.data);
      } catch (e) {
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <Info className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h2>
        <p className="text-gray-600 mb-8 max-w-md">
          The product you're looking for might have been removed or the link is incorrect.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-semibold transition-all hover:shadow-lg"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>
      </div>
    );
  }

  const avgRating = product.average_rating || 0;
  const ratingsCount = product.ratings_count || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Breadcrumbs */}
        <nav className="flex mb-8 items-center text-sm text-gray-500 overflow-x-auto whitespace-nowrap pb-2 sm:pb-0">
          <Link to="/" className="hover:text-green-600 transition">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900 font-medium truncate">{product.name}</span>
        </nav>

        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
          <div className="lg:grid lg:grid-cols-2 lg:gap-0">
            {/* Image Section */}
            <div className="p-4 sm:p-8 lg:p-12 bg-gray-50 flex items-center justify-center">
              <div className="w-full max-w-sm aspect-square relative rounded-2xl overflow-hidden bg-white shadow-inner group">
                <img
                  src={product.image_url || 'https://via.placeholder.com/800x800?text=No+Image'}
                  alt={product.name}
                  className="w-full h-full object-cover object-center transform transition-transform duration-500 group-hover:scale-110"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/800x800?text=No+Image'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none"></div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < Math.floor(avgRating) ? 'text-yellow-400 fill-current' : 'text-gray-200'}`}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-500">
                    {avgRating.toFixed(1)} ({ratingsCount} reviews)
                  </span>
                </div>

                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 leading-tight">
                  {product.name}
                </h1>
                
                <p className="text-3xl font-bold text-green-600 mb-6">
                  {product.formatted_price}
                </p>

                <div className="prose prose-sm text-gray-600 mb-8 max-w-none">
                  <p className="whitespace-pre-wrap leading-relaxed">
                    {product.description}
                  </p>
                </div>

                {/* Features */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-green-50/50 border border-green-100/50">
                    <Truck className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-sm font-bold text-gray-900">Fast Delivery</p>
                      <p className="text-xs text-gray-500">2-4 business days</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-blue-50/50 border border-blue-100/50">
                    <ShieldCheck className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-bold text-gray-900">100% Organic</p>
                      <p className="text-xs text-gray-500">Certified products</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/login')}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all shadow-lg hover:shadow-green-200 active:scale-95"
                  >
                    <ShoppingBag className="w-5 h-5" />
                    Buy Now
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="inline-flex items-center justify-center px-8 py-4 rounded-2xl font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all active:scale-95"
                  >
                    Return to Shop
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProduct;


